import React, { useEffect, useState, ChangeEvent, FormEvent } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useBooleanFlagValue } from '@worktango/feature-flags-react';
import { goto } from '~/adapters/location';
import { initializeAnonymousPendo } from '~/adapters/pendo';
import { uiSelectors } from '~/behaviors/selectors/ui.selectors';
import { uiActions } from '~/behaviors/actions/ui.actions';
import { accountActions } from '~/behaviors/actions/account.actions';
import { useAppDispatch, useAppSelector } from '~/behaviors/redux';
import { useTranslation } from '~/behaviors/languages';
import { contactUsUrl } from '~/config/settings/worktango.settings';
import { mui } from '~/tools/mui';
import { icons } from '~/tools/icons';
import { jss } from '~/tools/jss';
import { validate } from '~/tools/validate';
import { errors } from '~/tools/errors';
import Button from '~/components/Button';
import { PasswordInput, Alert } from '../UI.shared';
import { urls } from '~/tools/urls';

const useStyles = jss.makeStyles((theme) => ({
  cardContent: {
    padding: 24,
    width: 476,
  },
  form: {
    textAlign: 'center',
    minHeight: 250,
  },
  instructionText: {
    fontSize: 18,
  },
  inputContainer: {
    marginTop: 24,
  },
  inputLabel: {
    fontSize: 14,
  },
  input: {
    fontSize: 14,
    height: 40,
    boxSizing: 'border-box',
    padding: '8px 16px',
  },
  submitBtnContainer: {
    margin: '24px 0px 0px 0px',
  },
  errText: {
    paddingTop: theme.spacing(2),
  },
  navLinkContainer: {
    marginTop: 24,
  },
  circularProgressRoot: {
    color: theme.palette.common.white,
  },
  divider: {
    margin: '48px 0 24px 0',
  },
  footerText: {
    fontSize: 14,
  },
  alert: {
    marginBottom: '1rem',
  },
  appLink: {
    fontWeight: 'bold',
    display: 'inline-block',
    color: 'inherit',
  },
}));

const MODE = {
  SSO: 'sso',
  PASSWORD: 'password',
} as const;

type Mode = (typeof MODE)[keyof typeof MODE];

const LoginPage: React.FC = () => {
  const styles = useStyles(undefined);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const [email, setEmail] = useState<string>('');
  const [emailError, setEmailError] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [loginError, setLoginError] = useState<string>('');
  const [mode, setMode] = useState<Mode>(MODE.SSO);

  const showPendoOnLogin = useBooleanFlagValue('show-pendo-on-login', false);

  useEffect(() => {
    if (showPendoOnLogin) {
      initializeAnonymousPendo();
    }
  }, [showPendoOnLogin]);

  const { isAuthenticating, error: uiError } = useAppSelector(uiSelectors.selectAccountUI());

  const queryParams = urls.parseQueryString(location.search);
  const showMigratedMessage = queryParams?.migrated === 'true';
  const hasTokenExpiredError = uiError?.name?.includes(errors.types.tokenExpired);
  const hasActivationError = uiError?.name?.includes(errors.types.activation);

  const isValidSSOForm = mode === MODE.SSO && email !== '';
  const isValidPasswordForm = mode === MODE.PASSWORD && email !== '' && password !== '';
  const isValidForm = (isValidSSOForm || isValidPasswordForm) && !emailError;

  const submitLabel = mode === MODE.SSO ? 'next' : 'header.signin';

  const submitButtonContent = !isAuthenticating ? (
    t(submitLabel)
  ) : (
    <mui.CircularProgress classes={{ root: styles.circularProgressRoot }} size={24} />
  );

  const handleClickEditEmail = () => {
    setLoginError('');
    setMode(MODE.SSO);
  };

  const handleFormSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (emailError) return;

    switch (mode) {
      case MODE.SSO:
        // @ts-ignore @TODO Epic WT-21600
        dispatch(uiActions.changeAccountUI({ forgotPasswordEmail: email }));
        // @ts-ignore @TODO Epic WT-21600
        dispatch(accountActions.checkSSO(email)).then((action) => {
          if (action.error) {
            console.log(action);
            setLoginError(action.error.message);
          } else if (!action?.payload) {
            setMode(MODE.PASSWORD);
          } else {
            goto(action.payload);
          }
        });
        break;
      case MODE.PASSWORD:
      default:
        // @ts-ignore @TODO Epic WT-21600
        dispatch(accountActions.loginWithPassword({ email, password })).then((action) => {
          if (action?.error) {
            if (action.error.message === 'Rejected' && action.payload?.type === 'lockout') {
              // @ts-ignore @TODO Epic WT-21600
              return dispatch(uiActions.changeAccountUI({ isLockedOut: true })).then(() =>
                navigate('/forgot-password'),
              );
            }

            setLoginError(action.payload.message);
          }
        });
    }
  };

  const handleEmailChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newEmail = e.target.value;

    setEmail(newEmail);
    setEmailError('');
    if (newEmail.trim() !== '' && !validate.isEmail(newEmail)) {
      setEmailError('invalid.email.format');
    }
  };

  const inputStyle = {
    classes: {
      input: styles.input,
    },
  };

  const editAdornmentIcon = {
    classes: inputStyle.classes,
    endAdornment: (
      <mui.InputAdornment position="end">
        <Button
          color="grey"
          aria-label="edit e-mail"
          onClick={handleClickEditEmail}
          size="small"
          startIcon={<icons.Edit />}
          data-testid="email-edit-button"
        >
          {t('change')}
        </Button>
      </mui.InputAdornment>
    ),
  };

  return (
    <mui.CardContent className={styles.cardContent} data-testid="login-page">
      {showMigratedMessage && (
        // @ts-ignore @TODO Epic WT-21600
        <Alert title={t('login.migrated.success')} severity="info" className={styles.alert}>
          {t('login.migrated.desc.first')}
          <Link to="https://app.worktango.com" className={styles.appLink}>
            https://app.worktango.com
          </Link>
          {t('login.migrated.desc.last')}
        </Alert>
      )}

      {hasTokenExpiredError && (
        // @ts-ignore @TODO Epic WT-21600
        <Alert title={t('login.expired.title')} severity="warning" className={styles.alert}>
          {t('login.expired.desc')}
        </Alert>
      )}

      {hasActivationError && (
        // @ts-ignore @TODO Epic WT-21600
        <Alert title={t('user.activation.codeNotFound')} severity="warning" className={styles.alert}>
          {t('user.activation.loginOrGetInvited')}
        </Alert>
      )}

      <form className={styles.form} onSubmit={handleFormSubmit}>
        <mui.Typography className={styles.instructionText}>
          {t(mode === MODE.SSO ? 'login.instruction.email' : 'login.instruction.password')}
        </mui.Typography>

        <mui.TextField
          id="email"
          name="email"
          autoComplete="email"
          aria-label="email"
          className={styles.inputContainer}
          required
          fullWidth
          autoFocus
          variant="outlined"
          value={email}
          label={t('account.email')}
          onChange={handleEmailChange}
          inputProps={{ 'data-testid': 'email' }}
          InputProps={mode === MODE.SSO ? inputStyle : editAdornmentIcon}
          InputLabelProps={{
            classes: {
              root: styles.inputLabel,
            },
          }}
          size="small"
          disabled={mode !== MODE.SSO}
          error={!!emailError}
          helperText={t(emailError)}
        />

        {mode === MODE.PASSWORD && (
          <PasswordInput
            // @ts-ignore @TODO Epic WT-21600
            id="password"
            name="password"
            size="small"
            autoComplete="password"
            aria-label="password"
            className={styles.inputContainer}
            required
            toggleVisibility
            fullWidth
            autoFocus
            variant="outlined"
            value={password}
            label={t('account.password')}
            onChange={(e) => setPassword(e.target.value)}
            inputProps={{ 'data-testid': 'password' }}
            InputProps={{ classes: inputStyle.classes }}
            InputLabelProps={{ classes: { root: styles.inputLabel } }}
            inputRef={(input) => input && input.focus()}
          />
        )}

        {loginError && (
          <mui.FormHelperText error id="error-text" data-testid="error">
            {loginError}
          </mui.FormHelperText>
        )}

        <div className={styles.submitBtnContainer}>
          <Button
            aria-label="submit-button"
            fullWidth
            color="primary"
            variant="contained"
            type="submit"
            disabled={!isValidForm}
            data-testid="submit"
          >
            {submitButtonContent}
          </Button>
        </div>

        {mode === MODE.PASSWORD && (
          <div className={styles.navLinkContainer}>
            {/*@ts-ignore @TODO Epic WT-21600*/}
            <Link className={styles.link} data-testid="forgot" to="/forgot-password">
              {t('user.login.forgot')}
            </Link>
          </div>
        )}

        <mui.Divider className={styles.divider} />

        <div>
          <mui.Typography className={styles.footerText}>{t('login.improve.question')}</mui.Typography>
          {/*@ts-ignore @TODO Epic WT-21600*/}
          <a className={styles.link} href={contactUsUrl} target="_blank" rel="noopener noreferrer">
            {t('contact.us')}
          </a>
        </div>
      </form>
    </mui.CardContent>
  );
};

export default LoginPage;
